import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, ArrowDownToLine, ArrowUpFromLine, Loader2, SlidersHorizontal } from "lucide-react";
import StoreModal from "./StoreModal";
import { useHrGetStaffQuery } from "../../hr/api/hrApi";
import { useGetDepartmentsQuery } from "../../department/api/departmentsApi";
import {
  MovementType,
  useStoreCreateMovementMutation,
  useStoreGetItemsQuery,
  useStoreGetStoresQuery,
} from "../api/storesApi";
import { errMsg, fmtQty, staffName, todayIso } from "../utils/stores";

const inputCls =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
const labelCls = "block text-xs font-medium text-gray-600 mb-1";

const TITLES: Record<MovementType, string> = {
  in: "Receive stock",
  out: "Issue stock",
  adjustment: "Adjust stock",
};

const ICONS: Record<MovementType, React.ReactNode> = {
  in: <ArrowDownToLine className="w-4 h-4 text-green-600" />,
  out: <ArrowUpFromLine className="w-4 h-4 text-amber-600" />,
  adjustment: <SlidersHorizontal className="w-4 h-4 text-violet-600" />,
};

/**
 * Records one movement. The type is chosen up front (the row's Receive/Issue
 * buttons pass it in) because the fields that matter differ: a receipt asks who
 * supplied it, an issue asks who took it, an adjustment just asks why.
 */
export default function MovementModal({
  type: initialType,
  item,
  storeId: initialStoreId,
  onClose,
}: {
  type: MovementType;
  /** Preselected item when opened from a row; otherwise the form asks. */
  item?: any | null;
  /** Preselected store when opened from a store's own card. */
  storeId?: string | number | null;
  onClose: () => void;
}) {
  const [type, setType] = useState<MovementType>(initialType);
  const [error, setError] = useState<string | null>(null);

  const { data: itemsData } = useStoreGetItemsQuery({ status: "active" }, { skip: !!item });
  const { data: staffData } = useHrGetStaffQuery({}, { skip: type !== "out" });
  const { data: departments = [] } = useGetDepartmentsQuery(undefined, { skip: type !== "out" });
  const { data: stores = [] } = useStoreGetStoresQuery({ active_only: true });
  const [create, { isLoading }] = useStoreCreateMovementMutation();

  const [form, setForm] = useState({
    store_item_id: item?.id ? String(item.id) : "",
    // Which store this movement happens in. Preselected when opened from a
    // store, or when there is only one — so the common case is unchanged for
    // the secretariat.
    store_id: initialStoreId ? String(initialStoreId) : "",
    quantity: "",
    unit_cost: item?.unit_cost != null ? String(item.unit_cost) : "",
    movement_date: todayIso(),
    reference: "",
    supplier: "",
    issued_to_admin_id: "",
    issued_to: "",
    department_id: "",
    note: "",
  });

  const items = useMemo(() => itemsData?.rows ?? [], [itemsData]);
  const selected = item ?? items.find((i: any) => String(i.id) === form.store_item_id);

  // One store: pick it for them. The question only exists because there can be
  // more than one.
  useEffect(() => {
    if (!form.store_id && stores.length === 1) {
      setForm((f) => ({ ...f, store_id: String(stores[0].id) }));
    }
  }, [stores, form.store_id]);

  /** The store this was opened from — stated rather than asked for. */
  const lockedStore = useMemo(
    () => (initialStoreId ? stores.find((s: any) => String(s.id) === String(initialStoreId)) : null),
    [stores, initialStoreId],
  );

  /** What the chosen store holds of the chosen item — not the total everywhere. */
  const inThisStore = useMemo(() => {
    if (!selected?.balances || !form.store_id) return null;
    const row = selected.balances.find((b: any) => String(b.store_id) === form.store_id);
    return row ? Number(row.quantity) : 0;
  }, [selected, form.store_id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create({
        store_item_id: form.store_item_id,
        store_id: form.store_id,
        type,
        quantity: Number(form.quantity),
        // Recorded on the movement as what this consignment cost, if the item
        // carries a price — it never changes the item's own price.
        unit_cost: type === "in" && selected?.unit_cost != null ? Number(selected.unit_cost) : undefined,
        movement_date: form.movement_date,
        reference: form.reference.trim() || undefined,
        supplier: type === "in" ? form.supplier.trim() || undefined : undefined,
        issued_to_admin_id: type === "out" ? form.issued_to_admin_id || undefined : undefined,
        issued_to: type === "out" ? form.issued_to.trim() || undefined : undefined,
        department_id: type === "out" ? form.department_id || undefined : undefined,
        note: form.note.trim() || undefined,
      }).unwrap();
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <StoreModal title={TITLES[type]} icon={ICONS[type]} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Movement type *</label>
          <div className="grid grid-cols-3 gap-2">
            {(["in", "out", "adjustment"] as MovementType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`px-3 py-2 text-xs border rounded-lg capitalize transition-colors ${
                  type === t ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {t === "in" ? "Receipt" : t === "out" ? "Issue" : "Adjustment"}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>Item *</label>
          {item ? (
            <div className="px-3 py-2 rounded-lg bg-gray-50 border text-sm">
              <span className="font-medium text-gray-900">{item.name}</span>
              <span className="text-gray-500"> · {item.code}</span>
            </div>
          ) : (
            <select
              required
              value={form.store_item_id}
              onChange={(e) => setForm((f) => ({ ...f, store_item_id: e.target.value }))}
              className={inputCls}
            >
              <option value="">Select item…</option>
              {items.map((i: any) => (
                <option key={i.id} value={i.id}>
                  {i.name} ({i.code}) — {fmtQty(i.quantity)} {i.unit}
                </option>
              ))}
            </select>
          )}
          {selected && (
            <p className="text-xs text-gray-500 mt-1">
              In stock across all stores: {fmtQty(selected.quantity)} {selected.unit}
            </p>
          )}
        </div>

        <div>
          <label className={labelCls}>Store *</label>
          {/* Opened from a store's card: which store is a fact, not a choice. */}
          {lockedStore ? (
            <div className="px-3 py-2 rounded-lg bg-gray-50 border text-sm font-medium text-gray-800">
              {lockedStore.name}{lockedStore.location ? ` — ${lockedStore.location}` : ""}
            </div>
          ) : (
            <select
              required
              value={form.store_id}
              onChange={(e) => setForm((f) => ({ ...f, store_id: e.target.value }))}
              className={inputCls}
            >
              <option value="">Select store…</option>
              {stores.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.location ? ` — ${s.location}` : ""}
                </option>
              ))}
            </select>
          )}
          {selected && inThisStore !== null && (
            <p className="text-xs text-gray-500 mt-1">
              This store holds {fmtQty(inThisStore)} {selected.unit}
              {type !== "in" && inThisStore === 0 ? " — nothing to take out of it." : ""}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>
              Quantity{selected ? ` (${selected.unit})` : ""} *
            </label>
            <input
              type="number"
              required
              step="0.01"
              min={type === "adjustment" ? undefined : 0.01}
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              className={inputCls}
            />
            {type === "adjustment" && (
              <p className="text-xs text-gray-500 mt-1">Use a negative number to write stock off.</p>
            )}
          </div>
          <div>
            <label className={labelCls}>Date *</label>
            <input
              type="date"
              required
              value={form.movement_date}
              onChange={(e) => setForm((f) => ({ ...f, movement_date: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        {type === "in" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Supplier</label>
              <input
                value={form.supplier}
                onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))}
                placeholder="Who it came from"
                className={inputCls}
              />
            </div>
            {/* The price belongs to the ITEM and is set when the item is
                created or edited — not here. One receipt typed with a wrong
                figure must not revalue every unit already on the shelves. */}
            <div>
              <label className={labelCls}>Price</label>
              <div className="px-3 py-2 rounded-lg bg-gray-50 border text-sm text-gray-600">
                {selected?.unit_cost != null
                  ? `₦${Number(selected.unit_cost).toLocaleString()} per ${selected.unit}`
                  : "No price set on this item"}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Set on the item itself, under Items.
              </p>
            </div>
          </div>
        )}

        {type === "out" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Issued to (staff)</label>
                <select
                  value={form.issued_to_admin_id}
                  onChange={(e) => setForm((f) => ({ ...f, issued_to_admin_id: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">Select staff…</option>
                  {(staffData?.rows ?? []).map((s: any) => (
                    <option key={s.id} value={s.id}>{staffName(s) || s.staff_no}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Department</label>
                <select
                  value={form.department_id}
                  onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">Select department…</option>
                  {(departments as any[]).map((d: any) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Or issued to (name)</label>
              <input
                value={form.issued_to}
                onChange={(e) => setForm((f) => ({ ...f, issued_to: e.target.value }))}
                placeholder="For anyone not on the staff list — a chapter, a contractor…"
                className={inputCls}
              />
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Reference</label>
            <input
              value={form.reference}
              onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
              placeholder="Waybill / invoice / requisition no."
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Note</label>
            <input
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />} Record
          </button>
        </div>
      </form>
    </StoreModal>
  );
}
